import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  DsBoxModule,
  DsButtonModule,
  DsFormFieldModule,
  DsLoadingAreaConfiguration,
  DsLoadingAreaService,
  DsModalModule,
  DsSelectModule,
  DsToastModel,
  DsToastService,
  SelectListOption,
} from '@bmw-ds/components';
import { FleetStartupResponse } from '../../models/startup.model';
import { AppService } from '../../services/app.service';
import { DealerBiddingService } from '../../services/bidding.service';
import { AccessUserRequest } from '../../models/user.model';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { HstService } from '../../services/hst.service';
import { ApiResponse } from '../../models/parameter.model';
import { UserStateService } from '../../services/user-state.service';

@Component({
  selector: 'app-landing',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DsButtonModule,
    DsFormFieldModule,
    DsSelectModule,
    DsBoxModule,
    DsModalModule,
  ],
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.scss'],
})
export class LandingComponent implements OnInit {
  policyAcknowledged: boolean = false;
  fleetData?: FleetStartupResponse;
  loadingAreaConfig: DsLoadingAreaConfiguration = { id: 'bodyLoading' };
  private readonly dbConfigService = inject(DealerBiddingService);
  newApplicationModalOpen = false;
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly loadingAreaService = inject(DsLoadingAreaService);
  private readonly appService = inject(AppService);
  private readonly toastService = inject(DsToastService);
  private readonly hstService = inject(HstService);
  private readonly userStateService = inject(UserStateService);
  disclaimerText: string = '';
  errorMessage: string = '';
  requestSuccess = false;
  ngOnInit(): void {
    this.loadDealers();
  }
  dealerOptions: { id: string; label: string }[] = [];
  roleOptions: SelectListOption[] = [
    { id: '00', label: 'Viewer' },
    { id: '01', label: 'Buyer' },
    { id: '03', label: 'Manager' },
  ];
  userForm = {
    names: '',
    dealerCode: '',
    email: '',
    dealer: '',
    cNumber: '',
    role: '',
  };
  selectedDealer: string = '';
  submitUserForm() {
    const submitAccessArea: DsLoadingAreaConfiguration = {
      id: 'bodyLoading',
      loadingTitle: 'Submitting',
      loadingText: 'Access Request',
    };
    this.loadingAreaService.addLoadingArea(submitAccessArea);

    // Add these methods (you can add them after your other methods, around line 800)
    const accessRequest: AccessUserRequest = {
      userId: this.userForm.cNumber,
      dealerCode: this.userForm.dealerCode,
      role: this.userForm.role,
      name:
        this.userStateService.givenName() ||
        this.userForm.names.trim().split(' ').slice(0, -1).join(' '),
      surname:
        this.userStateService.surname() ||
        this.userForm.names.trim().split(' ').slice(-1)[0],
      email: this.userForm.email,
      dealerName: this.userForm.dealer,
    };

    this.dbConfigService.requestAccess(accessRequest).subscribe({
      next: (response) => {
        if (!response.success) {
          this.addToast(
            'critical',
            response.error?.message || 'Error submitting access request.'
          );
          this.loadingAreaService.removeLoadingArea(submitAccessArea.id);
          this.cdr.detectChanges();
          return;
        }
        this.requestSuccess = true;
        this.loadingAreaService.removeLoadingArea(submitAccessArea.id);
        this.cdr.detectChanges();
      },
      error: () => {
        this.addToast('critical', 'Error submitting access request.');
        this.loadingAreaService.removeLoadingArea(submitAccessArea.id);
        this.cdr.detectChanges();
      },
    });
  }
  loadDealers() {
    this.dbConfigService.getDbConfig().subscribe((config) => {
      if (config.success) {
        this.disclaimerText = config.data?.Disclaimer_Text || '';
      } else {
        this.addToast('critical', config.error?.message || 'Failed to load config');
      }
    });
    this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
    this.fetchDealers().subscribe({
      next: (response) => {
        if (!response.success) {
          this.addToast(
            'critical',
            response.error?.message || 'Failed to load dealers'
          );
          this.cdr.detectChanges();
          return;
        }
        this.mapToOptions(response.data);
        this.cdr.detectChanges();
      },
      error: (err) => console.error('Failed to load dealers', err),
      complete: () => {
        this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
      },
    });
  }
  private mapToOptions(dealers: any) {
    this.dealerOptions =
      dealers.agentList?.map((dealer: any) => ({
        id: dealer.code.length === 4 ? '0' + dealer.code : dealer.code,
        label: `${dealer.name} - ${dealer.code}`,
      })) ?? [];
  }

  private fetchDealers() {
    const userId = this.userStateService.userId();
    const dealerObservable = this.hstService.getUserAgentList(userId);
    return dealerObservable.pipe(
      tap((response) => {
        if (response.success && response.data?.agentList) {
          this.mapToOptions(response.data);
        }
      }),
      catchError((error) => {
        console.error('Error fetching dealer list:', error);
        return of({
          success: false,
          data: null,
          error: { code: 'HST_ERROR', message: 'Error fetching dealer list' },
        } as ApiResponse<any>);
      })
    );
  }
  enterApp() {
    const cNumber = this.userStateService.userId();
    const userInfoLoadingArea: DsLoadingAreaConfiguration = {
      id: 'bodyLoading',
      loadingTitle: 'Loading',
      loadingText: 'User Information',
    };
    this.loadingAreaService.addLoadingArea(userInfoLoadingArea);
    this.dbConfigService.confirmUserExists({ cNumber }).subscribe({
      next: (response) => {
        if (!response.success) {
          this.loadingAreaService.removeLoadingArea(userInfoLoadingArea.id);
          this.errorMessage =
            response.error?.message ||
            'Error checking user access. If this error persists please contact IT Support.';
          return;
        }
        const exists = response.data === true;
        if (exists) {
          if (this.selectedDealer) {
            this.dbConfigService
              .confirmUserDealerAccess({
                cNumber: cNumber,
                dealerCode: this.selectedDealer,
              })
              .subscribe({
                next: (accessResponse) => {
                  if (!accessResponse.success) {
                    this.loadingAreaService.removeLoadingArea(
                      userInfoLoadingArea.id
                    );
                    this.errorMessage =
                      accessResponse.error?.message ||
                      'Error checking user access. If this error persists please contact IT Support.';
                    return;
                  }
                  const accessStatus = accessResponse.data;
                  this.loadingAreaService.removeLoadingArea(
                    userInfoLoadingArea.id
                  );
                  console.log(accessStatus);
                  if (accessStatus?.accessStatus === 'ACCESS_GRANTED') {
                    // Save roleId from backend response
                    if (accessStatus?.role_id) {
                      localStorage.setItem('roleId', accessStatus.role_id);
                    } else {
                      localStorage.removeItem('roleId');
                    }

                    if (this.policyAcknowledged) {
                      localStorage.setItem('policyAcknowledged', 'true');
                      localStorage.setItem(
                        'dealerCode',
                        this.selectedDealer || ''
                      );
                      this.router.navigate(['/stock-search']);
                    }
                  } else if (
                    accessStatus?.accessStatus === 'USER_DEALER_NOT_FOUND'
                  ) {
                    localStorage.removeItem('roleId');
                    this.setDefaultValuesOnApplicationForm();
                    this.newApplicationModalOpen = true;
                  } else if (accessStatus?.accessStatus === 'ACCESS_DENIED') {
                    localStorage.removeItem('roleId');
                    this.errorMessage =
                      'Please ask your manager to approve your access or contact IT SUPPORT.';
                  } else {
                    localStorage.removeItem('roleId');
                    this.errorMessage =
                      'Unexpected access status received. Please contact IT Support.';
                  }
                },
                error: () => {
                  this.loadingAreaService.removeLoadingArea(userInfoLoadingArea.id);
                  this.errorMessage = 'Error checking user access. If this error persists please contact IT Support.';
                },
              });
          }
        } else {
          this.newApplicationModalOpen = true;
          this.setDefaultValuesOnApplicationForm();
          this.loadingAreaService.removeLoadingArea(userInfoLoadingArea.id);
          this.cdr.detectChanges();
        }
      },
      error: () => {
        this.newApplicationModalOpen = true;
        this.loadingAreaService.removeLoadingArea(userInfoLoadingArea.id);
      },
    });
  }
  private setDefaultValuesOnApplicationForm() {
    this.userForm.names = this.userStateService.employeeName();
    this.userForm.email = this.userStateService.email();
    this.userForm.cNumber = this.userStateService.userId();
    const selectedDealerObj = this.dealerOptions.find(
      (dealer) => dealer.id === this.selectedDealer
    );
    this.userForm.dealer = selectedDealerObj
      ? selectedDealerObj.label.split(' - ')[0]
      : '';
    const dealerCode = selectedDealerObj
      ? selectedDealerObj.label.split(' - ')[1]
      : '';
    this.userForm.dealerCode =
      dealerCode.length === 4 ? '0' + dealerCode : dealerCode;
  }

  addToast(tone: 'critical' | 'positive' | 'neutral', teastText: string): void {
    this.toastService.pushToast({
      id: 'error-toast',
      tone: tone,
      toastText: teastText,
    } as DsToastModel);
  }
  isAccessFormValid(): boolean {
    const { cNumber, dealerCode, role, names, email, dealer } = this.userForm;
    return !!(cNumber && dealerCode && role && names && email && dealer);
  }
}
